import { Popover } from '@headlessui/react';
import filtersIcon from 'assets/svg/icons/filters.svg';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import Count from 'components/atoms/Count';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import isEmpty from 'lodash/isEmpty';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, useContext, useMemo, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { VisibilityContext } from 'react-horizontal-scrolling-menu';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';
import type { FiltersConfigV2 } from 'types/shared/filters';
import { ChevronLeftIconOutline, ChevronRightIconOutline } from 'utils/icons';
import { OptionsV2 } from './FilterCell';
import SearchBar from './SearchBar';

const arrowsContainerClasses = 'absolute h-full top-1/2 -translate-y-1/2 z-10';

const arrowsClasses = 'bg-white shadow-md p-1.5 rounded-full text-primary-400';

const LeftArrow = () => {
  const { isFirstItemVisible, scrollPrev, initComplete } =
    useContext(VisibilityContext);

  return (
    <Button
      asDefault
      className={classNames(
        'left-0',
        '-translate-x-1/2',
        arrowsContainerClasses,
        {
          hidden: isFirstItemVisible || !initComplete,
        },
      )}
      onClick={(e) => {
        e.stopPropagation();
        scrollPrev();
      }}
    >
      <div className={arrowsClasses}>
        <ChevronLeftIconOutline className="w-4" />
      </div>
    </Button>
  );
};

const RightArrow = () => {
  const { isLastItemVisible, scrollNext, initComplete } =
    useContext(VisibilityContext);

  return (
    <Button
      asDefault
      className={classNames(
        'right-0',
        'translate-x-1/2',
        arrowsContainerClasses,
        {
          hidden: isLastItemVisible || !initComplete,
        },
      )}
      onClick={(e) => {
        e.stopPropagation();
        scrollNext();
      }}
    >
      <div className={arrowsClasses}>
        <ChevronRightIconOutline className="w-4" />
      </div>
    </Button>
  );
};

const getFilterCount = (filter: { type: string }, value: any) => {
  const emptyPlaceholder = 0;

  if (filter.type === 'checkbox') {
    if (isEmpty(value)) return emptyPlaceholder;
    return value.length;
  }
  if (filter.type === 'radio') {
    if (!value) return emptyPlaceholder;
    return 1;
  }

  if (filter.type === 'date-range') {
    if (isEmpty(value)) return emptyPlaceholder;
    return 1;
  }
  return emptyPlaceholder;
};

const themeBackground: Record<string, string> = {
  expenses: 'bg-expenses-100',
  revenues: 'bg-revenues-100',
  taxes: 'bg-taxes-100',
  bank: 'bg-bank-100',
  primary: 'bg-primary-100',
};

const themeText: Record<string, string> = {
  expenses: 'text-expenses',
  revenues: 'text-revenues',
  taxes: 'text-taxes',
  bank: 'text-bank',
  primary: 'text-primary',
};

export const FiltersV3: FC<{
  filtersConfig: FiltersConfigV2;
  filters: {
    key: string;
    label: string;
    type: string;
    options: { label: string; value: string }[];
  }[];
  isLoading: boolean;
  isError: boolean;
  searchPlaceholder?: string;
  onClickToggle?: () => void;
  containerClassName?: string;
}> = ({
  filtersConfig,
  filters,
  isLoading,
  searchPlaceholder,
  containerClassName,
}) => {
  const { t } = useTranslation();
  const world = useWorldTheme();

  const { filtersData, onChange, onSearch, clearFilters } = filtersConfig || {};

  const [_filter, setActiveFilter] = useState<typeof filters[number]>();

  const activeFilter = (_filter || filters[0]) as
    | typeof filters[number]
    | undefined;

  const [referenceElement, setReferenceElement] = useState<any>();

  const [popperElement, setPopperElement] = useState<any>();

  const containerElm = useMemo(
    () => document.querySelector('#LAYOUT_CONTENT'),
    [],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 25,
          boundary: containerElm || undefined,
        },
      },
    ],
    placement: 'bottom-end',
  });

  const totalFiltersCount = filters.reduce(
    (agg, current) => agg + getFilterCount(current, filtersData[current.key]),
    0,
  );

  return (
    <div className={classNames('flex flex-1 gap-3', containerClassName)}>
      <SearchBar
        placeholder={searchPlaceholder}
        searchValue={filtersData?.text as string}
        setSearchValue={onSearch}
        className="flex flex-1"
      />
      <Popover as={Fragment}>
        {({ open, close }) => (
          <>
            <Popover.Button as={Fragment}>
              <Button
                asDefault
                ref={setReferenceElement}
                className="ring-primary-100 ring-1 flex shadow-listControl items-center gap-2 rounded-lg hover:bg-primary-50 p-2"
                onClick={() => setActiveFilter(filters[0])}
              >
                {totalFiltersCount ? (
                  <span className="min-w-[1.25rem] flex justify-center">
                    <Count count={totalFiltersCount} />
                  </span>
                ) : (
                  <img src={filtersIcon} className="w-[1.25rem] opacity-85" />
                )}
                <span className="first-letter:capitalize inline-block">
                  {t('keywords.keyword_filter')}
                </span>
              </Button>
            </Popover.Button>

            {createPortal(
              <AnimatePresence>
                {open && (
                  <div
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                    className="z-10 mt-1"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        transition: { duration: 0.1, ease: 'easeOut' },
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.95,
                        transition: { duration: 0.1, ease: 'easeIn' },
                      }}
                    >
                      <Popover.Panel static>
                        <div className="shadow-lg rounded-lg">
                          <div className="flex items-start bg-white w-[38rem] h-[360px] rounded-lg border border-primary-100 relative">
                            <div className="flex flex-col w-[35%] flex-shrink-0 h-full pt-2">
                              <div className="flex-1 overflow-auto force-scroll-style pb-2 px-2 flex flex-col items-start justify-start text-left">
                                {filters.map((filter) => {
                                  const countValue = getFilterCount(
                                    filter,
                                    filtersData[filter.key],
                                  );

                                  const themeClassName = `${
                                    themeBackground[world] ||
                                    themeBackground.primary
                                  } ${themeText[world] || themeText.primary}`;

                                  const isActive =
                                    activeFilter?.key === filter.key;

                                  return (
                                    <Button
                                      key={filter.key}
                                      asDefault
                                      className={classNames(
                                        'flex px-4 py-2 items-center rounded-lg justify-between w-full text-init text-primary-700 text-sm font-semibold',
                                        {
                                          [themeClassName]: isActive,
                                        },
                                      )}
                                      onClick={() => setActiveFilter(filter)}
                                    >
                                      <span>{filter.label}</span>

                                      <div>
                                        {countValue ? (
                                          <span
                                            className={classNames(
                                              'w-[1.15rem] h-[1.15rem] rounded-full flex items-center justify-center text-xs font-semibold',
                                              {
                                                [themeClassName]: !isActive,
                                                'bg-white': isActive,
                                                [themeText[world] ||
                                                themeText.primary]: isActive,
                                              },
                                            )}
                                          >
                                            {countValue}
                                          </span>
                                        ) : null}
                                      </div>
                                    </Button>
                                  );
                                })}
                              </div>

                              <div className="p-2 border-t border-t-primary-100">
                                <Button
                                  structure="secondary"
                                  color="red"
                                  className="!border-transparent !shadow-none w-full"
                                  onClick={() => clearFilters()}
                                >
                                  {t('reset_all')}
                                </Button>
                              </div>
                            </div>

                            {!!activeFilter && (
                              <div className="w-[65%] flex-shrink-0 h-full flex flex-col items-start justify-star border-l border-l-primary-100">
                                <div className="p-4 w-full border-b border-b-primary-100">
                                  <h3 className="text-primary-900 font-semibold text-sm">
                                    {activeFilter.label}
                                  </h3>
                                </div>

                                <OptionsV2
                                  filter={activeFilter}
                                  onChange={onChange}
                                  value={
                                    activeFilter.key
                                      ? filtersData[activeFilter.key]
                                      : null
                                  }
                                />

                                <div className="p-2 border-t border-t-primary-100 w-full flex gap-2">
                                  <Button
                                    structure="secondary"
                                    color="light-red"
                                    className="flex-1"
                                    onClick={() => {
                                      const key = activeFilter.key;
                                      if (key) clearFilters([key]);
                                    }}
                                  >
                                    {t('clear')}
                                  </Button>
                                  <Button
                                    className="flex-1"
                                    onClick={() => close()}
                                  >
                                    {t('done')}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </Popover.Panel>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>,
              document.body,
            )}
          </>
        )}
      </Popover>
    </div>
  );
};
